import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useItineraryContext } from './ItineraryContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import type { MapCameraPosition } from 'tripPlanner/api/types'
import { extractItineraryMarkers } from '../hooks/itineraryMapMarkers'
import {
  MapStyleProvider,
} from 'tripPlanner/components/ChatMap/MapStyleContext'
import type { MapStyleMode } from 'tripPlanner/components/ChatMap/map3DConfig'

export interface MapInteractionContextValue {
  /** Complete saved itinerary, independent of the full map's active filters. */
  itineraryResult: ReturnType<typeof extractItineraryMarkers>
  getDrawerCamera: (entryId: string) => MapCameraPosition | undefined
  setDrawerCamera: (entryId: string, camera: MapCameraPosition) => void
  /** Called when a hotel card is hovered (from carousel or explore drawer). Updates isCardHoveredRef + hoveredHotelId. */
  onCardHover: (offerId: string | undefined) => void
  /** Called when a map marker is hovered. Guards against clearing hover while a card is actively hovered. */
  onMarkerHover: (markerId: string | undefined) => void
  /** Currently highlighted hotel/marker ID for visual sync between map and card lists */
  highlightedMarkerId: string | undefined
  /** Shared main-map style mode so drawer maps can stay in sync */
  mapStyleMode: MapStyleMode
  onMapStyleModeChange: (mode: MapStyleMode) => void
}

const MapInteractionContext = createContext<MapInteractionContextValue | undefined>(undefined)

interface MapInteractionProviderProps {
  value: Omit<MapInteractionContextValue, 'itineraryResult' | 'getDrawerCamera' | 'setDrawerCamera'>
  children: ReactNode
}

/**
 * Page-level interaction sync (hover + style). Also mounts `MapStyleProvider` so
 * shared chat components can read style without importing this page module.
 */
function MapInteractionProvider({ value, children }: MapInteractionProviderProps) {
  const { itineraryData } = useItineraryContext()
  const { chatKey } = useChatStreamStatus()
  const drawerCameras = useRef(new Map<string, MapCameraPosition>())
  useEffect(() => { drawerCameras.current.clear() }, [chatKey])
  const getDrawerCamera = useCallback((entryId: string) => drawerCameras.current.get(entryId), [])
  const setDrawerCamera = useCallback((entryId: string, camera: MapCameraPosition) => {
    drawerCameras.current.set(entryId, camera)
  }, [])
  const itineraryResult = useMemo(() => extractItineraryMarkers(itineraryData), [itineraryData])
  const contextValue = useMemo(() => ({ ...value, itineraryResult, getDrawerCamera, setDrawerCamera }), [value, itineraryResult, getDrawerCamera, setDrawerCamera])
  return (
    <MapInteractionContext.Provider value={contextValue}>
      <MapStyleProvider
        mapStyleMode={value.mapStyleMode}
        onMapStyleModeChange={value.onMapStyleModeChange}
      >
        {children}
      </MapStyleProvider>
    </MapInteractionContext.Provider>
  )
}

export function useMapInteraction(): MapInteractionContextValue {
  const ctx = useContext(MapInteractionContext)
  if (!ctx) throw new Error('useMapInteraction must be used within MapInteractionProvider')
  return ctx
}

export {
  MapInteractionProvider,
}
